import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  useMutation,
  UseMutationResult,
  useQueryClient
} from '@tanstack/react-query';
import {fundsApi} from '../api';
import {CreateFundDTO, FundWithMetaModel, FundsFilter, UpdateFundParams} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type FundsState = {
  getInfiniteFunds: UseInfiniteQueryResult<InfiniteData<FundWithMetaModel>, Error>;
  filters: FundsFilter;
  updateFilter: (newFilter: Partial<FundsFilter>) => void;
  getFundById: ReturnType<typeof useQuery>;
  createFundMutation: UseMutationResult<unknown, Error, CreateFundDTO>;
  updateFundMutation: UseMutationResult<unknown, Error, UpdateFundParams>;
  deleteFundMutation: UseMutationResult<unknown, Error, string>;
  refetchFund: () => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useFunds = (initialFilter: FundsFilter = {}): FundsState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FundsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteFunds = useInfiniteQuery<
    FundWithMetaModel,
    Error,
    InfiniteData<FundWithMetaModel>,
    ['funds', 'infinite', Omit<FundsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['funds', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return fundsApi.getFunds({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
  });

  const refetchFund = useCallback(() => {
    queryClient.invalidateQueries({queryKey: ['fund', initialFilter.query], refetchType: 'all'});
    queryClient.invalidateQueries({queryKey: ['funds', 'infinite', infiniteQueryFilters], refetchType: 'all'});
  }, [queryClient, initialFilter.query, infiniteQueryFilters]);

  const getFundById = useQuery({
    queryKey: ['fund', initialFilter.query],
    queryFn: () => {
      if (!initialFilter.query) {
        return Promise.resolve(null);
      }
      return fundsApi.getFundById(initialFilter.query);
    },
    enabled: !!initialFilter.query,
  });

  const createFundMutation = useMutation({
    mutationFn: fundsApi.createFund,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['funds', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Фонд успешно создан',
        name: 'createFund',
        theme: 'success',
      });
    },
  });

  const updateFundMutation = useMutation({
    mutationFn: fundsApi.updateFund,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fund', initialFilter.query], refetchType: 'all'});
      queryClient.invalidateQueries({queryKey: ['funds', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Фонд успешно обновлен',
        name: 'updateFund',
        theme: 'success',
      });
    },
  });

  const deleteFundMutation = useMutation({
    mutationFn: (fundId: string) => fundsApi.deleteFund(fundId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['funds', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Фонд успешно удален',
        name: 'deleteFund',
        theme: 'success',
      });
    },
  });

  const updateFilter = useCallback((newFilter: Partial<FundsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteFunds,
    filters,
    updateFilter,
    getFundById,
    createFundMutation,
    updateFundMutation,
    deleteFundMutation,
    refetchFund,
  };
}; 